import { ORDER_STATUS } from "../../../Shared/utils/constants";
import { tokenReplace } from "../../../Shared/utils/tokenReplacement";
import { SubOrderSummaryGroupText, } from "../subscriptionsUtils/subscriptions-types";
import { CurrentBreakpoint } from "./ToggleableOrdersList-types";
/**
 * Group past cut off orders and active/upcoming orders for pause/cancellation. Sets the title and tooltip for each section.
 *
 * @param orderList
 * @returns
 */
export const groupOrders = (orderList, tokenMap) => {
    const ordersPastCutoff = {
        title: SubOrderSummaryGroupText.TITLE_PAST_CUTOFF,
        orders: orderList
            .filter((o) => o.IsPastCutoff && !o.IsPastDelivery)
            .map((order) => {
            return Object.assign(Object.assign({}, order), { canToggle: false, defaultIsChecked: false });
        }),
        tooltip: tokenReplace(SubOrderSummaryGroupText.TOOLTIP_PAST_CUTOFF, false, tokenMap),
        activeOrdersWarning: null,
    };
    // when canceling / pausing a sub, future orders will be deleted but renewal orders can be kept
    // completed orders also can not not canceled
    const upcomingOrders = {
        title: SubOrderSummaryGroupText.TITLE_UPCOMING,
        orders: orderList
            .filter((o) => !o.IsPastCutoff && !o.IsPastDelivery)
            .map((order) => {
            return Object.assign(Object.assign({}, order), { canToggle: order.OrderStatus != ORDER_STATUS.FUTURE && order.OrderStatus != ORDER_STATUS.COMPLETE, defaultIsChecked: order.OrderStatus != ORDER_STATUS.COMPLETE });
        }),
        tooltip: SubOrderSummaryGroupText.TOOLTIP_UPCOMING,
        activeOrdersWarning: null,
    };
    return [ordersPastCutoff, upcomingOrders];
};
/**
 * This is used to group orders for editing via the Edit Subscription Flow.
 * @param orderList
 * @param store
 * @returns
 */
export const groupOrdersForEditing = (orderList, store) => {
    // Group orders that are past the cutoff
    const ordersPastCutoff = {
        title: SubOrderSummaryGroupText.TITLE_PAST_CUTOFF,
        orders: orderList
            .filter((o) => o.IsPastCutoff && !o.IsPastDelivery) // Filter for orders past the cutoff
            .map((order) => (Object.assign(Object.assign({}, order), { canToggle: false, defaultIsChecked: false }))),
        tooltip: SubOrderSummaryGroupText.TOOLTIP_PAST_CUTOFF_UPDATE,
        activeOrdersWarning: null,
    };
    // Group upcoming orders
    const upcomingOrders = {
        title: SubOrderSummaryGroupText.TITLE_ACTIVE,
        orders: orderList
            .filter((o) => !o.IsPastCutoff && !o.IsPastDelivery) // Filter for upcoming orders
            .map((order) => (Object.assign(Object.assign({}, order), { canToggle: order.OrderStatus != ORDER_STATUS.COMPLETE, defaultIsChecked: order.OrderStatus != ORDER_STATUS.COMPLETE }))),
        tooltip: SubOrderSummaryGroupText.TOOLTIP_UPDATE_ACTIVE,
        activeOrdersWarning: SubOrderSummaryGroupText.ACTIVE_ORDERS_WARNING,
    };
    // Filter orders in orderGroups based on OrderProgramId and OrderDeliveryScheduleId
    [ordersPastCutoff, upcomingOrders].forEach((group) => {
        group.orders = group.orders.filter((order) => order.OrderProgramId === store.model.SubscriptionProgramId &&
            order.OrderDeliveryScheduleId === store.model.SubscriptionDeliveryScheduleId);
    });
    // Initialize and filter selected orders (only orders without meals are selected by default)
    const selectedOrders = upcomingOrders.orders.filter((order) => !order.HasSelectedRecipe);
    return {
        orderGroups: [ordersPastCutoff, upcomingOrders],
        selectedOrders,
    };
};
/**
 * This is used in the ToggleableOrdersListModal, not the Edit Flow/SubscriptionSummaryPage.
 * Initialize state with all orders that should be selected by default.
 *
 * @param orderGroups
 * @returns
 */
export const selectedOrdersInitializer = (orderGroups) => {
    return (orderGroups === null || orderGroups === void 0 ? void 0 : orderGroups.flatMap((orderGroup) => orderGroup.orders.filter((o) => o.defaultIsChecked))) || [];
};
/**
 * Controls toggling orders in the ToggleableOrdersList
 *
 * @param selectedOrders
 * @param setSelectedOrders
 * @returns
 */
export const toggleOrder = (selectedOrders, setSelectedOrders) => (order) => {
    if (selectedOrders.includes(order)) {
        setSelectedOrders(selectedOrders.filter((o) => o !== order));
    }
    else {
        setSelectedOrders([...selectedOrders, order]);
    }
};
/**
 * Controls toggling orders in the ToggleableOrdersList on the Subscription Summary Page.
 * @param subscriptionOrdersState
 * @param setSubscriptionOrdersState
 * @returns
 */
export const toggleSubscriptionOrdersState = (subscriptionOrdersState, setSubscriptionOrdersState) => (order) => {
    const selectedOrders = subscriptionOrdersState.selectedOrders;
    if (selectedOrders.includes(order)) {
        setSubscriptionOrdersState((prev) => (Object.assign(Object.assign({}, prev), { selectedOrders: selectedOrders.filter((o) => o !== order) })));
    }
    else {
        setSubscriptionOrdersState((prev) => (Object.assign(Object.assign({}, prev), { selectedOrders: [...selectedOrders, order] })));
    }
};
/**
 * Function to handle the expansion of the order details in the ToggleableOrdersList.
 * @param setExpanded
 * @param orderId
 */
export const handleAccordionChange = (setter, orderId) => {
    setter((prevExpanded) => prevExpanded.includes(orderId) ? prevExpanded.filter((i) => i !== orderId) : [...prevExpanded, orderId]);
};
/**
 * Filter the conditional elements based on the grid template areas.
 * This is used to determine which elements should be displayed in the summary and details sections of the accordion.
 */
export const filterConditionalElementsLists = (gridTemplateAreas, orders) => {
    const result = {};
    // Split the string into an array and trim each value to remove extra quotes or spaces
    const areasArray = gridTemplateAreas.split(" ").map((area) => area.replace(/"/g, "").trim());
    // Create a Set from the cleaned array
    const areasSet = new Set(areasArray);
    orders.forEach((order) => {
        const conditionalElementsList = [
            {
                id: "chip",
                summaryDisplayCondition: areasSet.has("chip") && order.HasSelectedRecipe,
                // The chip is only displayed in the summary (when the order has selected recipes).
                detailsDisplayCondition: false,
            },
            {
                id: "total",
                summaryDisplayCondition: areasSet.has("total") && !!order.Total,
                detailsDisplayCondition: !areasSet.has("total") && !!order.Total,
            },
            {
                id: "fulfillment",
                summaryDisplayCondition: areasSet.has("fulfillment"),
                detailsDisplayCondition: !areasSet.has("fulfillment"),
            },
            {
                id: "orderNumber",
                summaryDisplayCondition: areasSet.has("orderNumber"),
                detailsDisplayCondition: !areasSet.has("orderNumber"),
            },
        ];
        const summaryElements = conditionalElementsList.filter(({ summaryDisplayCondition }) => summaryDisplayCondition);
        const detailsElements = conditionalElementsList.filter(({ detailsDisplayCondition }) => detailsDisplayCondition);
        const hasDetailsElements = detailsElements.length > 0;
        result[order.OrderId] = {
            summaryElements,
            detailsElements,
            hasDetailsElements,
        };
    });
    return result;
};
/**
 *
 * @param breakpoint
 * @returns
 */
export const getDateFormat = (breakpoint) => {
    const xxDateFormat = "M/D";
    const xsDateFormat = "MMM. Do";
    const smDateFormat = "MMMM Do, YYYY";
    if (breakpoint === CurrentBreakpoint.XX) {
        return xxDateFormat;
    }
    else if (breakpoint === CurrentBreakpoint.XS) {
        return xsDateFormat;
    }
    else {
        return smDateFormat;
    }
};
/**
 *
 * @param isXS
 * @param isSmall
 * @param isMedium
 * @param isLarge
 * @returns
 */
export const getCurrentBreakpoint = (isXS, isSmall, isMedium, isLarge) => {
    if (isLarge)
        return CurrentBreakpoint.LG;
    if (isMedium)
        return CurrentBreakpoint.MD;
    if (isSmall)
        return CurrentBreakpoint.SM;
    if (isXS)
        return CurrentBreakpoint.XS;
    return CurrentBreakpoint.XX;
};
